// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { LocationType } from 'domains/models/event_type/locationType';
import { EventTypeUserListItem } from 'presentations/api/schema/event_type_user';
import Image from 'presentations/views/components/atoms/Image';
import Icon from 'presentations/views/components/atoms/Icon';
import Link from 'presentations/views/components/atoms/Link';
import {
  fontSize,
  fontWeight,
  mediaMax,
  spacing,
} from 'presentations/views/styles';
import { gray01 } from 'presentations/views/styles/color';
import Location from './components/Location';
import { memo } from 'react';
import { HostOperationType } from 'domains/models/form/hostOperationType';
import { helpPath } from 'presentations/routes/path';

export type Props = {
  host: EventTypeUserListItem;
  hostOperationType: HostOperationType;
  hostImage: string | null;
  teamImage: string | null;
  teamName: string | null;
  eventName: string;
  otherUsersCount: number;
  scheduleText: string;
  timeZoneText: string;
  location?: {
    locationType: LocationType;
    address: string | null;
    access: string | null;
    customLocation: string | null;
  };
  isRequest?: boolean;
};

const Component = memo((props: Props) => {
  const t = translateInComponent(translations);

  const isVisibleHostName = props.hostOperationType !== HostOperationType.Team;
  const image = isVisibleHostName ? props.hostImage : props.teamImage;
  const name = isVisibleHostName ? props.host.name : props.teamName;

  const teamName =
    props.hostOperationType === HostOperationType.None ? props.teamName : null;

  return (
    <StyledContainer>
      <StyledHostContainer>
        {image && <StyledHostImage src={image} alt={name || ''} />}
        <StyledHostContent>
          <StyledHostNameContainer>
            <StyledHostName>{name}</StyledHostName>
            {isVisibleHostName && props.otherUsersCount > 0 && (
              <StyledOtherUsersCount>
                {t('Other contacts', {
                  num: props.otherUsersCount,
                })}
              </StyledOtherUsersCount>
            )}
          </StyledHostNameContainer>
          {teamName && <StyledTeamName>{teamName}</StyledTeamName>}
        </StyledHostContent>
      </StyledHostContainer>
      <StyledEventContent>
        <StyledEventName>{props.eventName}</StyledEventName>
        {props.scheduleText && (
          <StyledEventScheduleText>
            {props.scheduleText}
          </StyledEventScheduleText>
        )}
        {props.isRequest ? (
          <StyledRequestSystem>
            <StyledIcon name="tag-check-outline" size={fontSize(18)} />
            <StyledRequestSystemText>
              {t('request system')}
            </StyledRequestSystemText>
            <StyledHelpLink href={helpPath('8293281')} target="_blank">
              <StyledRequestIcon name="help" size={fontSize(18)} />
            </StyledHelpLink>
          </StyledRequestSystem>
        ) : null}
        <StyledTimeZone>
          <StyledIcon
            color="inherit"
            name="clock-time-nine-outline"
            size={fontSize(18)}
          />
          <StyledTimeZoneText>{props.timeZoneText}</StyledTimeZoneText>
        </StyledTimeZone>
        {props.location && (
          <StyledLocationContainer>
            <Location
              locationType={props.location.locationType}
              address={props.location.address}
              access={props.location.access}
              customLocation={props.location.customLocation}
            />
          </StyledLocationContainer>
        )}
      </StyledEventContent>
    </StyledContainer>
  );
});

export default Component;

const kfFadeIn = keyframes`
  100% {
    opacity: 1;
  }
`;

const StyledContainer = styled.div``;
const StyledHostContainer = styled.div`
  display: flex;
  ${mediaMax.tablet} {
    display: none;
  }
`;
const StyledHostImage = styled(Image)`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  margin-right: ${spacing(16)};
  object-fit: cover;
  ${mediaMax.tablet} {
    width: 50px;
    height: 50px;
  }
`;
const StyledHostContent = styled.div``;
const StyledHostNameContainer = styled.div`
  display: flex;
  align-items: center;
`;
const StyledHostName = styled.div`
  font-size: ${fontSize(24)};
  font-weight: ${fontWeight('bold')};
  ${mediaMax.tablet} {
    font-size: ${fontSize(20)};
  }
`;
const StyledOtherUsersCount = styled.div`
  margin-left: ${spacing(8)};
  font-size: ${fontSize(18)};
`;
const StyledTeamName = styled.div`
  font-size: ${fontSize(14)};
  color: ${gray01};
`;
const StyledEventContent = styled.div`
  margin-top: ${spacing(16)};
`;
const StyledEventName = styled.div`
  font-size: ${fontSize(20)};
  font-weight: ${fontWeight('bold')};
  line-height: 1.48;
  ${mediaMax.tablet} {
    font-size: ${fontSize(18)};
  }
`;
const StyledEventScheduleText = styled.div`
  font-size: ${fontSize(16)};
  font-weight: ${fontWeight('bold')};
  line-height: 1.48;
  opacity: 0;
  animation: ${kfFadeIn} 0.5s ease-out both;
`;
const StyledTimeZone = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${spacing(8)};
`;
const StyledIcon = styled(Icon)``;
const StyledTimeZoneText = styled.div`
  font-size: ${fontSize(14)};
  color: ${gray01};
  line-height: 1.6;
  margin-left: ${spacing(6)};
`;
const StyledLocationContainer = styled.div`
  opacity: 0;
  animation: ${kfFadeIn} 0.5s ease-out both;
`;
const StyledRequestSystem = styled(StyledTimeZone)``;
const StyledHelpLink = styled(Link)`
  margin-left: ${spacing(4)};
  align-items: center;
  display: flex;
`;
const StyledRequestIcon = styled(Icon)`
  color: ${gray01};
`;
const StyledRequestSystemText = styled(StyledTimeZoneText)``;
